/**
 * The five ticket states, and nothing else.
 *
 * Jua defines exactly five status pills. The job lifecycle has seventeen
 * statuses, so this file's real job is the mapping — collapsing seventeen
 * internal states onto the five a person needs to tell apart at a glance.
 *
 * **Every pill carries its word.** That is not belt-and-braces: this console is
 * read outdoors in direct sun on cheap screens, where a pale sand and a pale
 * blue are genuinely hard to separate, and it is read by people who cannot
 * separate them at all. The colour is a second signal, never the only one.
 *
 * The pairs themselves live in `index.css` as `.state-*`, so "assigned" cannot
 * drift into two slightly different blues on two screens.
 */

type StateKey = 'new' | 'assigned' | 'progress' | 'resolved' | 'overdue';

/**
 * Seventeen statuses onto five pills.
 *
 * The groupings are about what somebody does next, not about how the state
 * machine is built:
 *
 *  · **new** — nobody owns it yet. Includes DISPATCHING, because a job still
 *    looking for a technician is not yet anybody's.
 *  · **assigned** — it has a name against it and has not started moving.
 *  · **progress** — somebody is on it, *or* it is deliberately parked waiting
 *    on a customer, a part or access. Both are "in flight, not idle".
 *  · **resolved** — the work is done, whether or not the paperwork is.
 *  · **overdue** — something needs a person now. Only DISPATCH_FAILED, which
 *    means every channel was tried and nobody was reached.
 */
const STATE: Record<string, StateKey> = {
  DRAFT: 'new',
  NEW: 'new',
  TRIAGED: 'new',
  DISPATCHING: 'new',

  ASSIGNED: 'assigned',

  EN_ROUTE: 'progress',
  ON_SITE: 'progress',
  IN_PROGRESS: 'progress',
  PENDING_PARTS: 'progress',
  PENDING_CUSTOMER: 'progress',
  PENDING_ACCESS: 'progress',
  ON_HOLD: 'progress',

  WORK_DONE: 'resolved',
  VERIFIED: 'resolved',
  CLOSED: 'resolved',

  DISPATCH_FAILED: 'overdue',

  // Cancelled is not a state anybody acts on, so it takes the neutral pill and
  // says so with a strikethrough rather than borrowing the red one.
  CANCELLED: 'new',
};

const CLASS: Record<StateKey, string> = {
  new: 'state-new',
  assigned: 'state-assigned',
  progress: 'state-progress',
  resolved: 'state-resolved',
  overdue: 'state-overdue',
};

/** Sentence case, and the words a dispatcher would say out loud. */
const LABEL: Record<string, string> = {
  DRAFT: 'Draft',
  NEW: 'New',
  TRIAGED: 'Triaged',
  DISPATCHING: 'Finding a technician',
  ASSIGNED: 'Assigned',
  EN_ROUTE: 'On the way',
  ON_SITE: 'On site',
  IN_PROGRESS: 'In progress',
  PENDING_PARTS: 'Waiting on parts',
  PENDING_CUSTOMER: 'Waiting on customer',
  PENDING_ACCESS: 'Waiting on access',
  ON_HOLD: 'On hold',
  WORK_DONE: 'Work done',
  VERIFIED: 'Verified',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
  DISPATCH_FAILED: 'Nobody reached',
};

/** Turns SCREAMING_SNAKE into sentence case for anything not in the map. */
function sentence(value: string): string {
  const words = value.replace(/_/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function StatusBadge({ status, overdue = false }: { status: string; overdue?: boolean }) {
  // A breached SLA outranks the lifecycle state: a job that is in progress and
  // three hours past its target needs to read as overdue, because that is the
  // thing a dispatcher has to act on.
  const key: StateKey = overdue ? 'overdue' : (STATE[status] ?? 'new');
  const struck = status === 'CANCELLED' ? ' line-through' : '';

  return (
    <span className={`badge ${CLASS[key]}${struck}`}>
      {overdue ? 'Overdue' : (LABEL[status] ?? sentence(status))}
    </span>
  );
}

/**
 * Priority.
 *
 * Deliberately drawn from the same five pills rather than a sixth palette.
 * P1 borrows the overdue pair because a P1 is the thing you drop other work
 * for, and giving it its own red would mean two reds meaning two different
 * urgent things.
 */
const PRIORITY: Record<string, StateKey> = {
  P1: 'overdue',
  P2: 'progress',
  P3: 'assigned',
  P4: 'new',
};

const PRIORITY_LABEL: Record<string, string> = {
  P1: 'P1 · critical',
  P2: 'P2 · high',
  P3: 'P3 · normal',
  P4: 'P4 · low',
};

export function PriorityBadge({ priority }: { priority: string }) {
  return (
    <span className={`badge ${CLASS[PRIORITY[priority] ?? 'new']}`}>
      {PRIORITY_LABEL[priority] ?? priority}
    </span>
  );
}
